import { memoize } from 'micro-memoize';
import { DriverNodeList } from '@futrli-sdk/predict';

import type {
  FormulaNonFinancialDriver,
  FreestyleNonFinancialDriver,
  NonFinancialDriverDuplicateData,
  NonFinancialDriverFormulaOptionsResponse,
  NonFinancialDriverGroupSaveData,
  NonFinancialDriverGroupUpdateData,
  NonFinancialDriverNodeList,
  NonFinancialDriverValues
} from '../../../predict/src/core/api/types/non-financial-drivers';
import { ApiView, ApiViewState, getDefaultApiViewState } from '../../core/ApiView';
import {
  createGroupMutation,
  deleteDriverMutation,
  duplicateDriverMutation,
  updateDriverOrderingMutation,
  updateGroupAndOrderingMutation,
  updateGroupMutation
} from '../../mutations/nonFinancialDrivers';
import { buildDriverTree, DriverTree } from '../../processors/nonFinancialDrivers/driversTree';
import { driversDataQuery, driversOrderQuery, formulaOptionsQuery } from '../../queries/nonFinancialDrivers';

export interface MutateCallbacks<TData> {
  onSuccess?: (data: TData) => void;
  onError?: (error: Error) => void;
}

export interface NonFinancialDriversViewState extends ApiViewState {
  driverTree?: DriverTree;
  driversData?: NonFinancialDriverValues[];
  driversOrder?: NonFinancialDriverNodeList[];
  errorCreatingGroup: Error | null;
  errorDeletingDriver: Error | null;
  errorDuplicatingDriver: Error | null;
  errorUpdatingGroup: Error | null;
  errorUpdatingGroupAndOrdering: Error | null;
  errorUpdatingOrdering: Error | null;
  formulaOptions?: NonFinancialDriverFormulaOptionsResponse;
  isCreatingGroup: boolean;
  isDeletingDriver: boolean;
  isDuplicatingDriver: boolean;
  isUpdatingTree: boolean;
  isUpdatingGroup: boolean;
  isUpdatingGroupAndOrdering: boolean;
  isUpdatingOrdering: boolean;
}

export class NonFinancialDriversView extends ApiView {
  private driversDataObs: ReturnType<typeof driversDataQuery>;
  private driversOrderObs: ReturnType<typeof driversOrderQuery>;
  private formulaOptionsObs: ReturnType<typeof formulaOptionsQuery>;

  private createGroupMut: ReturnType<typeof createGroupMutation>;
  private deleteDriverMut: ReturnType<typeof deleteDriverMutation>;
  private duplicateDriverMut: ReturnType<typeof duplicateDriverMutation>;
  private updateGroupMut: ReturnType<typeof updateGroupMutation>;
  private updateDriverOrderingMut: ReturnType<typeof updateDriverOrderingMutation>;
  private updateGroupAndOrderingMut: ReturnType<typeof updateGroupAndOrderingMutation>;

  private memoizedBuildDriverTree = memoize(buildDriverTree);

  public state: NonFinancialDriversViewState = {
    ...getDefaultApiViewState(),
    driverTree: undefined,
    driversData: undefined,
    driversOrder: undefined,
    errorCreatingGroup: null,
    errorDeletingDriver: null,
    errorDuplicatingDriver: null,
    errorUpdatingGroup: null,
    errorUpdatingGroupAndOrdering: null,
    errorUpdatingOrdering: null,
    formulaOptions: undefined,
    isCreatingGroup: false,
    isDeletingDriver: false,
    isDuplicatingDriver: false,
    isUpdatingTree: false,
    isUpdatingGroup: false,
    isUpdatingGroupAndOrdering: false,
    isUpdatingOrdering: false
  };

  constructor(private workspaceId: string, private organisationId: string) {
    super();

    this.driversDataObs = this.addQuery(driversDataQuery(workspaceId, organisationId));
    this.driversOrderObs = this.addQuery(driversOrderQuery(workspaceId, organisationId));
    this.formulaOptionsObs = this.addQuery(formulaOptionsQuery(workspaceId, organisationId));

    this.createGroupMut = this.addMutation(createGroupMutation(workspaceId, organisationId));
    this.deleteDriverMut = this.addMutation(deleteDriverMutation(workspaceId, organisationId));
    this.duplicateDriverMut = this.addMutation(duplicateDriverMutation(workspaceId, organisationId));
    this.updateGroupMut = this.addMutation(updateGroupMutation(workspaceId, organisationId));
    this.updateDriverOrderingMut = this.addMutation(updateDriverOrderingMutation(workspaceId, organisationId));
    this.updateGroupAndOrderingMut = this.addMutation(updateGroupAndOrderingMutation(workspaceId, organisationId));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************

  public createGroup(data: NonFinancialDriverGroupSaveData, callbacks?: MutateCallbacks<void>) {
    return this.createGroupMut.mutate(data, callbacks);
  }

  public deleteDriver(driverId: string, callbacks?: MutateCallbacks<void>) {
    return this.deleteDriverMut.mutate(driverId, callbacks);
  }

  public deleteGroup(groupId: string, callbacks?: MutateCallbacks<void>) {
    return this.deleteDriverMut.mutate(groupId, callbacks);
  }

  public duplicateDriver(
    driverId: string,
    data: NonFinancialDriverDuplicateData,
    callbacks?: MutateCallbacks<{ data: FormulaNonFinancialDriver | FreestyleNonFinancialDriver }>
  ) {
    return this.duplicateDriverMut.mutate({ driverId, data }, callbacks);
  }

  public updateGroup(
    groupId: string,
    data: NonFinancialDriverGroupUpdateData,
    callbacks?: MutateCallbacks<void>
  ) {
    return this.updateGroupMut.mutate({ groupId, data }, callbacks);
  }

  public updateDriverOrdering(
    data: NonFinancialDriverNodeList[],
    callbacks?: MutateCallbacks<DriverNodeList[]>
  ) {
    return this.updateDriverOrderingMut.mutate(data, callbacks);
  }

  public updateGroupAndOrdering(
    groupId: string,
    groupData: NonFinancialDriverGroupUpdateData,
    orderingData: NonFinancialDriverNodeList[],
    callbacks?: MutateCallbacks<void>
  ) {
    return this.updateGroupAndOrderingMut.mutate({ groupId, groupData, orderingData }, callbacks);
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  private get driversData() {
    return this.getResult(this.driversDataObs).data?.data;
  }

  private get driversOrder() {
    return this.getResult(this.driversOrderObs).data;
  }

  private get driverTree() {
    return this.driversData && this.driversOrder
      ? this.memoizedBuildDriverTree(this.driversOrder, this.driversData)
      : undefined;
  }

  private get formulaOptions() {
    return this.getResult(this.formulaOptionsObs).data;
  }

  private get errorCreatingGroup() {
    return this.createGroupMut.getCurrentResult().error;
  }

  private get errorDeletingDriver() {
    return this.deleteDriverMut.getCurrentResult().error;
  }

  private get errorDuplicatingDriver() {
    return this.duplicateDriverMut.getCurrentResult().error;
  }

  private get errorUpdatingGroup() {
    return this.updateGroupMut.getCurrentResult().error;
  }

  private get errorUpdatingGroupAndOrdering() {
    return this.updateGroupAndOrderingMut.getCurrentResult().error;
  }

  private get errorUpdatingOrdering() {
    return this.updateDriverOrderingMut.getCurrentResult().error;
  }

  private get isCreatingGroup() {
    return this.createGroupMut.getCurrentResult().status === 'pending';
  }

  private get isDeletingDriver() {
    return this.deleteDriverMut.getCurrentResult().status === 'pending';
  }

  private get isDuplicatingDriver() {
    return this.duplicateDriverMut.getCurrentResult().status === 'pending';
  }

  private get isUpdatingGroup() {
    return this.updateGroupMut.getCurrentResult().status === 'pending';
  }

  private get isUpdatingGroupAndOrdering() {
    return this.updateGroupAndOrderingMut.getCurrentResult().status === 'pending';
  }

  private get isUpdatingOrdering() {
    return this.updateDriverOrderingMut.getCurrentResult().status === 'pending';
  }

  private get isUpdatingTree() {
    return (
      this.isCreatingGroup ||
      this.isDeletingDriver ||
      this.isDuplicatingDriver ||
      this.isUpdatingGroup ||
      this.isUpdatingGroupAndOrdering ||
      this.isUpdatingOrdering
    );
  }

  protected getNewState(): NonFinancialDriversViewState {
    return {
      ...super.getNewState(),
      driverTree: this.driverTree,
      driversData: this.driversData,
      driversOrder: this.driversOrder,
      errorCreatingGroup: this.errorCreatingGroup,
      errorDeletingDriver: this.errorDeletingDriver,
      errorDuplicatingDriver: this.errorDuplicatingDriver,
      errorUpdatingGroup: this.errorUpdatingGroup,
      errorUpdatingGroupAndOrdering: this.errorUpdatingGroupAndOrdering,
      errorUpdatingOrdering: this.errorUpdatingOrdering,
      formulaOptions: this.formulaOptions,
      isCreatingGroup: this.isCreatingGroup,
      isDeletingDriver: this.isDeletingDriver,
      isDuplicatingDriver: this.isDuplicatingDriver,
      isUpdatingTree: this.isUpdatingTree,
      isUpdatingGroup: this.isUpdatingGroup,
      isUpdatingGroupAndOrdering: this.isUpdatingGroupAndOrdering,
      isUpdatingOrdering: this.isUpdatingOrdering
    };
  }
}
